/**
 * Instance context for the frontend.
 *
 * Reads instance config from /api/config and provides it to the component tree.
 * Components can use useInstance() to access instance data (features, branding, etc.).
 *
 * Also applies per-instance theme (primary color → CSS variables) when branding
 * is configured. See applyInstanceTheme() for the token mapping.
 */

import { createContext, useContext, useEffect, useState, type ReactNode } from "react";

export interface InstanceFeatures {
  funding_direct_transfer: boolean;
  funding_changelly: boolean;
  funding_bridge: boolean;
  funding_deferred: boolean;
  batch_cheques: boolean;
  cheque_arbitrary_amounts: boolean;
  cheque_cross_instance: boolean;
  distribution_telegram: boolean;
  distribution_merchant_portal: boolean;
  billing_mode: "on_chain" | "invoice" | "hybrid";
}

export interface InstanceBranding {
  logo_url?: string;
  primary_color?: string;
  secondary_color?: string;
  cheque_template?: string;
  display_name?: string;
}

export interface InstanceSettings {
  denominations: number[]; // allowed amounts when cheque_arbitrary_amounts=false
}

export interface InstanceInfo {
  slug: string;
  name: string;
  /**
   * Token symbol (e.g. "USDC", "USDT", "EURT", "EURC", future: "XAUt").
   * Not a literal union — the registry is DB-driven (tokens table) and new
   * symbols can be added without code changes. Callers that need token-aware
   * behavior should look up the row via useTokenRegistry().bySymbol(token).
   */
  token: string;
  /**
   * Chain the instance operates on. Lowercase. Defaults to "solana" for legacy
   * rows without an explicit value (server derives from instances.network).
   */
  network: "solana" | "ethereum";
  features: InstanceFeatures;
  branding: InstanceBranding;
  settings: InstanceSettings;
}

export interface PlatformBranding {
  logoUrl?: string;
  primaryColor?: string;
  tagline?: string;
}

interface InstanceContextValue {
  instance: InstanceInfo | null;
  platformBranding: PlatformBranding | null;
  demoMode: boolean;
  loading: boolean;
}

const InstanceContext = createContext<InstanceContextValue>({
  instance: null,
  platformBranding: null,
  demoMode: false,
  loading: true,
});

// ─── Theme application ───────────────────────────────────────────────────────

/**
 * Convert a 6-digit hex color to CSS HSL component string (e.g. "221 83% 53%").
 * Returns null if the input is invalid.
 */
function hexToHslComponents(hex: string): string | null {
  if (!/^#[0-9a-fA-F]{6}$/.test(hex)) return null;
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  if (max === min) return `0 0% ${Math.round(l * 100)}%`;
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h = 0;
  if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
  else if (max === g) h = ((b - r) / d + 2) / 6;
  else h = ((r - g) / d + 4) / 6;
  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

/**
 * Apply instance branding to CSS custom properties on the root element.
 * Calling with no branding (or no primary_color) resets to stylesheet defaults.
 *
 * Token mapping:
 *   branding.primary_color → --primary, --ring
 *   auto-derived from lightness → --primary-foreground
 */
export function applyInstanceTheme(branding?: InstanceBranding | { primaryColor?: string; logoUrl?: string; tagline?: string }) {
  const root = document.documentElement;
  // Accept both snake_case (from /api/config) and camelCase (from admin branding object)
  const color = (branding as any)?.primary_color ?? (branding as any)?.primaryColor;

  if (color) {
    const hsl = hexToHslComponents(color);
    if (hsl) {
      const lightness = parseInt(hsl.split(" ")[2]); // "221 83% 53%" → 53
      const fg = lightness >= 60 ? "0 0% 7%" : "0 0% 100%";
      root.style.setProperty("--primary", hsl);
      root.style.setProperty("--primary-foreground", fg);
      root.style.setProperty("--ring", hsl);
      return;
    }
  }

  // Reset to stylesheet defaults
  root.style.removeProperty("--primary");
  root.style.removeProperty("--primary-foreground");
  root.style.removeProperty("--ring");
}

// ─── Provider ────────────────────────────────────────────────────────────────

export function InstanceProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<InstanceContextValue>({
    instance: null,
    platformBranding: null,
    demoMode: false,
    loading: true,
  });

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    let slug = params.get("instance");

    // URL path: /i/:slug/...
    if (!slug) {
      const match = window.location.pathname.match(/^\/i\/([a-z0-9][a-z0-9-]+[a-z0-9])\//);
      if (match) slug = match[1];
    }

    // Fallback: instance admin session stored in localStorage.
    // Scoped to /write and /instance/* to avoid bleeding into B2C pages.
    if (!slug) {
      const path = window.location.pathname;
      if (path === "/write" || path.startsWith("/instance/")) {
        try {
          const stored = localStorage.getItem("postcash_instance_user");
          if (stored) {
            const parsed = JSON.parse(stored);
            if (parsed?.instanceSlug) slug = parsed.instanceSlug;
          }
        } catch { /* ignore */ }
      }
    }

    const headers: Record<string, string> = {};
    if (slug) headers["X-Instance-Slug"] = slug;

    fetch(`${import.meta.env.VITE_API_URL || ""}/api/config`, { headers })
      .then((res) => res.json())
      .then((data) => {
        const inst = data.instance
          ? {
              ...data.instance,
              token: String(data.instance.token ?? "USDC"),
              network: (data.instance.network === "ethereum" ? "ethereum" : "solana") as "solana" | "ethereum",
              settings: { denominations: data.instance.settings?.denominations ?? [] },
            }
          : null;
        const platformBranding: PlatformBranding | null = data.platformBranding ?? null;
        setState({
          instance: inst,
          platformBranding,
          demoMode: data.demoMode || false,
          loading: false,
        });
        // Instance branding wins; fall back to platform branding when no instance.
        if (data.instance?.branding) {
          applyInstanceTheme(data.instance.branding);
        } else if (platformBranding) {
          applyInstanceTheme(platformBranding);
        } else {
          applyInstanceTheme(undefined);
        }
      })
      .catch(() => {
        setState((prev) => ({ ...prev, loading: false }));
      });

    // Reset theme on unmount (SPA navigation away from instance page)
    return () => applyInstanceTheme(undefined);
  }, []);

  return (
    <InstanceContext.Provider value={state}>
      {children}
    </InstanceContext.Provider>
  );
}

export function useInstance(): InstanceContextValue {
  return useContext(InstanceContext);
}
